import React, { useEffect, useState } from 'react'
import NumberInputWithNull from '@components/common/form/NumberInputWithNull'
import VendorBlacklist from '@components/groupOrders/VendorBlacklist'
import MenuItemBlacklist from '@components/groupOrders/MenuItemBlacklist'
import Checkbox from '@components/common/form/Checkbox'
import { MultiSelectDropDown } from '@components/common/form'
import {
  AddItemToBlacklistInput,
  AddVendorToBlacklistInput,
  VendorBlacklistEntry,
  ItemBlacklistEntry,
  UniversalMenuConfig,
  Option,
} from '@types'
import { Label } from '@res/styledComponents/index'
import { ClosedBlacklistedBtn } from '@components/groupOrders/VendorBlacklist'
import Modal from '@components/common/modal/Modal'

interface UniversalMenuConfigModalProps {
  clientSettingsId: string
  currentConfig?: UniversalMenuConfig
  isClientFlatPriceEnabled: boolean
  loadChefs: any
  loadMenuItems: any
  // Item Blacklist
  addItemToBlacklist: (
    csId: string,
    input: AddItemToBlacklistInput,
  ) => Promise<ItemBlacklistEntry>
  updateItemBlacklistEntry: (
    csId: string,
    input: { itemId: string; active: boolean; reason: string },
  ) => Promise<ItemBlacklistEntry>

  // Vendor Blacklist
  addVendorToBlacklist: (
    csId: string,
    input: AddVendorToBlacklistInput,
  ) => Promise<VendorBlacklistEntry>
  updateVendorBlacklistEntry: (
    csId: string,
    input: { vendorId: string; active: boolean; reason: string },
  ) => Promise<VendorBlacklistEntry>

  displayFailureMessage: (mssg: string) => void
  dismissInfoModal: () => void
  showCustomModal: (val: any) => void
  hideModal: () => void
  loadAllergens: () => Promise<Option[]>
  saveConfig: (condfig: UniversalMenuConfig) => void
}

const UniversalMenuConfigModal: React.FC<UniversalMenuConfigModalProps> = ({
  clientSettingsId,
  currentConfig,
  loadChefs,
  loadMenuItems,
  isClientFlatPriceEnabled,

  addItemToBlacklist,
  updateItemBlacklistEntry,
  addVendorToBlacklist,
  updateVendorBlacklistEntry,

  displayFailureMessage,
  dismissInfoModal,
  hideModal,
  loadAllergens,
  showCustomModal,
  saveConfig,
}) => {
  const [allergens, setAllergens] = useState<Option[]>([])
  const [enableSides, setEnableSides] = useState(
    currentConfig?.enableSides || false,
  )
  const [enableModifications, setEnabledModifications] = useState(
    currentConfig?.enableModifications || false,
  )
  const [excludedDietaryTags, setExcludedDietaryTags] = useState(
    currentConfig?.excludedDietaryTags || [],
  )
  const [maxItemPriceLimit, setMaxItemPriceLimit] = useState<number | null>(
    currentConfig?.maxItemPriceLimit || null,
  )
  const [maxModificationPriceLimit, setMaxModificationPriceLimit] = useState<
    number | null
  >(currentConfig?.maxModificationPriceLimit || null)
  const [maxTotalItemPriceLimit, setMaxItemTotalPriceLimit] = useState<
    number | null
  >(currentConfig?.maxTotalItemPriceLimit || null)
  const [flatPriceMarginMin, setFlatPriceMarginMin] = useState<number | null>(
    currentConfig?.flatPriceMarginMinimum || null,
  )
  const [vendorsBlacklist, setVendorBlacklist] = useState<
    VendorBlacklistEntry[]
  >(currentConfig?.vendorBlacklist || [])
  const [menuItemBlacklist, setMenuItemBlacklist] = useState<
    ItemBlacklistEntry[]
  >(currentConfig?.menuItemBlacklist || [])

  useEffect(() => {
    const fetchAllergens = async () => {
      const optionAllergens = await loadAllergens()
      setAllergens(optionAllergens)
    }

    fetchAllergens()
  }, [loadAllergens])

  const onVendorBlacklistChange = (nextList: VendorBlacklistEntry[]) => {
    setVendorBlacklist(nextList)
  }

  const onMenuItemBlacklistChange = (nextList: ItemBlacklistEntry[]) => {
    setMenuItemBlacklist(nextList)
  }

  const onOpenVendorsBlacklist = () => {
    showCustomModal({
      title: 'Vendor Blacklist',
      content: (
        <VendorBlacklist
          editable={true}
          list={vendorsBlacklist}
          addVendorToBlacklist={(req) =>
            addVendorToBlacklist(clientSettingsId, req)
          }
          updateVendorBlacklistEntry={(req) =>
            updateVendorBlacklistEntry(clientSettingsId, req)
          }
          onChange={(nextList) => onVendorBlacklistChange(nextList)}
          close={dismissInfoModal}
          loadChefs={loadChefs}
        />
      ),
    })
  }

  const onOpenMenuItemsBlacklist = () => {
    showCustomModal({
      title: 'Menu Items Blacklist',
      content: (
        <MenuItemBlacklist
          editable={true}
          list={menuItemBlacklist}
          addItemToBlacklist={(req) =>
            addItemToBlacklist(clientSettingsId, req)
          }
          updateItemBlacklistEntry={(req) =>
            updateItemBlacklistEntry(clientSettingsId, req)
          }
          onChange={(nextList) => onMenuItemBlacklistChange(nextList)}
          loadChefs={loadChefs}
          loadMenuItems={loadMenuItems}
          close={dismissInfoModal}
        />
      ),
    })
  }

  const onSaveConfig = () => {
    if (
      maxItemPriceLimit !== null &&
      maxTotalItemPriceLimit !== null &&
      maxItemPriceLimit > maxTotalItemPriceLimit
    ) {
      displayFailureMessage(
        'The max base item price cannot be greater than the total item price limit',
      )

      return
    }
    const config: UniversalMenuConfig = {
      enableSides,
      enableModifications,
      excludedDietaryTags,
      maxItemPriceLimit,
      maxModificationPriceLimit,
      maxTotalItemPriceLimit,
      flatPriceMarginMinimum: flatPriceMarginMin,
      vendorBlacklist: vendorsBlacklist,
      menuItemBlacklist: menuItemBlacklist,
    }
    saveConfig(config)
  }

  return (
    <Modal
      title="Universal Menu Configuration"
      maxWidth="800px"
      width="800px"
      hideModal={hideModal}
    >
      <div className="space-y-4">
        <p className="text-gray-600 my-6 p-4 shadow-md rounded-md border border-solid border-gray-200">
          Universal menu configurations dictate which menu items are available
          on the group order menu and what the client and chef prices are.
        </p>

        <div className="flex flex-col space-y-2 p-4 border border-solid border-gray-200 shadow-md rounded-md w-90p">
          <Label>Item Type Preferences</Label>
          <Checkbox
            label="Enable Sides"
            checked={enableSides}
            onChange={() => setEnableSides(!enableSides)}
          />
          <Checkbox
            label="Enable Modifications"
            checked={enableModifications}
            onChange={() => setEnabledModifications(!enableModifications)}
          />
        </div>

        <div className="flex flex-col space-y-2 p-4 w-90p  border border-solid border-gray-200 shadow-md rounded-md">
          <div className="flex flex-col py-4 px-2 w-full">
            <Label>Max Base Item Price Limit</Label>
            <NumberInputWithNull
              value={maxItemPriceLimit}
              onChange={(val) => setMaxItemPriceLimit(val)}
              className="w-1/2 h-10"
              min={0}
            />
          </div>
          <div className="flex flex-col py-4 px-2  border-t-2 border-solid border-gray-200">
            <Label>Max Modification Price Limit</Label>
            <NumberInputWithNull
              value={maxModificationPriceLimit}
              onChange={(val) => setMaxModificationPriceLimit(val)}
              className="w-1/2 h-10"
              min={0}
            />
          </div>
          <div className="flex flex-col py-4 px-2  border-t-2 border-solid border-gray-200">
            <Label>Max Total Price Limit (Base Item + Mods)</Label>
            <NumberInputWithNull
              value={maxTotalItemPriceLimit}
              onChange={(val) => setMaxItemTotalPriceLimit(val)}
              className="w-1/2 h-10"
              min={maxItemPriceLimit || 0}
            />
          </div>
          {isClientFlatPriceEnabled && (
            <div className="flex flex-col py-4 px-2  border-t-2 border-solid border-gray-200">
              <Label>Flat Price Margin Minimum</Label>
              <NumberInputWithNull
                value={flatPriceMarginMin}
                onChange={(val) => setFlatPriceMarginMin(val)}
                min={0}
                max={100}
                step={0.1}
                asPercent={true}
                precision={2}
                className="w-1/2 rounded-md h-10"
              />
            </div>
          )}
          <div className="flex flex-col py-4 px-2  border-t-2 border-solid border-gray-200">
            <MultiSelectDropDown
              label="Exclude Dietary Preferences"
              width="50%"
              includeSelectAll={false}
              renderCheckedOptions={true}
              options={allergens}
              checkedOptions={excludedDietaryTags}
              searchPlaceholder=""
              onChange={(newOptions) => setExcludedDietaryTags(newOptions)}
            />
          </div>
        </div>

        <div className="flex flex-row items-center space-x-2 p-4 shadow-md rounded-md border border-solid border-gray-200">
          <div className="flex flex-col">
            <Label>Vendor Blacklist</Label>
            <ClosedBlacklistedBtn
              label="Blacklisted Vendors"
              listCount={vendorsBlacklist.filter((e) => e.active).length}
              toggleOpen={onOpenVendorsBlacklist}
            />
          </div>
          <div className="flex flex-col">
            <Label>Menu Item Blacklist</Label>
            <ClosedBlacklistedBtn
              label="Blacklisted Menu Items"
              listCount={menuItemBlacklist.filter((e) => e.active).length}
              toggleOpen={onOpenMenuItemsBlacklist}
            />
          </div>
        </div>

        <div className="flex flex-row items-center">
          <button
            className="py-2 px-3 flex items-center bg-green-600 text-white font-semibold rounded-lg hover:bg-green-400"
            onClick={onSaveConfig}
          >
            Save Universal Menu Configuration
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default UniversalMenuConfigModal
